import type { ReactNode } from 'react'

/**
 * 作业屏的版式：图占满宽，读数与决定贴在图底下。排队、诊断、拍板、成品、失败，
 * 从头到尾都是这一个。
 *
 * 背景：这里先后试过三版。整页从上往下一段接一段（四个段头各带 48 像素上边距，
 *   一屏诊断要滚三屏半才看得到那颗「开始处理」）；然后是 7:3 两栏，图在左、
 *   判断在右（用户：「太长了……是不是图片是左边，然后诊断在右边，7:3 这样」）；
 *   两栏又被否：「这个排版我真没招了」「如果是老外，他们会怎么排版？」
 * 设计意图：照 Topaz / Magnific / Photoroom 那一类工具页——图是这一页，
 *   读数是图下一条芯片，决定是再底下一条操作栏。不再给读数和决定各自一块栏、
 *   各自一张卡：那是把「看图」和「拍板」拆成两件事，而它们本来就是同一眼。
 *
 *   成品与失败那两屏一度还留在 7:3 上，用户当场发现了这条缝（2026-09-11
 *   「结束之后的怎么又回到之前左右格式的版本了？？」），于是 7:3 那套整个撤掉。
 * 关键约束：图的高度上限压在**图**上，不压在外面那个槽上——槽的高度是 auto，
 *   而图的 max-height: 100% 是按槽高算的百分比，auto 解不出百分比，上限会形同虚设，
 *   图溢出去正好盖住紧贴在下面的操作栏（见 app.css 里 .review-fig 那几条）。
 */
export function Review({ image, children }: { image: ReactNode; children: ReactNode }) {
  return (
    <div className="review">
      <div className="review-fig">{image}</div>
      {children}
    </div>
  )
}

/**
 * 小标题。右栏里的每一段、折叠区里的每一块，都用它领头。
 *
 * 不用通用段头（.sec-head）：那一套是 24 像素标题加 48 像素上边距，为整幅宽度写的，
 * 三成宽的栏里连着摆三个，标题占的高度比它领着的内容还多。
 */
export function MiniHead({ t, note }: { t: string; note?: React.ReactNode }) {
  return (
    <div className="mini-h">
      <b>{t}</b>
      {note && <span>{note}</span>}
    </div>
  )
}

/**
 * 整幅宽的一节，用首屏那套段头领头。
 *
 * 背景：2026-09-12 用户指着完成页：「处理前的诊断要单拎出来，放到外面」「这里也应该
 *   有大标题以及做正式一点，不要像狗皮膏药这样」。三张诊断卡原先折在页脚那一折里，
 *   由 14 像素的小标题领着——摊开之后是三张正经的卡，标题却像张补丁贴在上面。
 * 设计意图：诊断是这一趟的正文之一（上面那些复诊读数逐项比的就是它），
 *   所以给它与案例区、历史区同级的段头（`.sec-head`：24 像素标题加一句说明）。
 * 关键约束：`MiniHead` 那一套是为三成宽的栏写的，别拿来领整幅宽的内容；
 *   反过来这套段头也别塞进窄栏——48 像素上边距加 24 像素标题，比它领着的内容还高。
 */
export function Section({ t, note, children }: {
  t: string; note?: string; children: ReactNode
}) {
  return (
    <section>
      <div className="sec-head">
        <h2>{t}</h2>
        {note && <p>{note}</p>}
      </div>
      {children}
    </section>
  )
}

/**
 * 折起来的那一段。
 *
 * 背景：决策轨迹与诊断读数是「凭什么这么判」的全部依据，原先各占一整段摊在结果页上，
 *   两段加起来就是一屏半——而拿到成品的人第一件事是下载，不是复读管线的自述。
 * 设计意图：依据一件不少；页末的事后轨迹默认折起来，诊断闸上的详细读数则由调用方
 *   `defaultOpen` 展开——那是用户决定是否继续的依据。摘要要写清里面是什么，
 *   否则一个只写「详情」的三角形等于把东西藏了。
 *
 *   `major` 是整幅宽那一档（页末的决策轨迹）：摘要本身就是段头——24 像素标题加一句
 *   说明，右侧一个「展开 / 收起」。2026-09-12 用户嫌原先那条 13.5 像素的灰条像狗皮膏药，
 *   而它领着的是整幅宽的七步轨迹。窄栏里的那一档（闸上的「详细读数」）仍用小标题，
 *   只是默认展开；它旁边就是价格和倒计时，标题一大就抢了那颗按钮的主角位。
 * 关键约束：折的只是**事后说明**。要用户拍板的东西（价格、会不会改画面、闸的倒计时）
 *   一律不折——那些折起来就是把该看见的代价藏起来。
 */
export function Fold({ t, note, major, defaultOpen = false, children }: {
  t: string; note?: string; major?: boolean; defaultOpen?: boolean; children: ReactNode
}) {
  return (
    <details className={major ? 'fold fold-major' : 'fold'} open={defaultOpen}>
      <summary>
        {major ? (
          <>
            <span className="fold-h">
              <h2>{t}</h2>
              {note && <p>{note}</p>}
            </span>
            <span className="fold-cue" aria-hidden="true" />
          </>
        ) : t}
      </summary>
      <div className="fold-in">{children}</div>
    </details>
  )
}
